/**
 * GraphVisualization Component
 * Renders React Flow graph from AI-generated DOM structure
 */

import React, { useCallback, memo, useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  ASL_GRAPH_NODE_HEIGHT,
  ASL_GRAPH_NODE_WIDTH,
  applyDagreTopBottom,
} from '@/utils/aslToReactFlow';
import useStickyGraph from '@stepflow/ui-shared/hooks/useStickyGraph';

// Custom node: shows logicalId, Comment appears on hover (Portal for layering)
const AgentNode = memo(({ data, selected }) => {
  const label = data?.label ?? '';
  const comment = data?.comment ?? '';
  const hasComment = comment.length > 0;
  /** Workbench Agents tab highlights the row being edited vs the default Brain purple card. */
  const highlighted = Boolean(data?.highlighted);
  /** LIVE run-path: this agent is AT PLAY right now in a running room — pulse it. */
  const active = Boolean(data?.active);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    if (!hasComment) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      x: rect.left + rect.width / 2,
      y: rect.top
    });
  };
  const handleMouseLeave = () => {
    setTooltip((t) => ({ ...t, show: false }));
  };

  return (
    <>
      <div
        className="group relative w-full h-full box-border overflow-hidden flex items-center"
        style={{
          // Agent node uses the app's muted slate-blue theme (tailwind custom
          // ``blue`` 500→700: #4a515c→#2f343c), not the old bright indigo/purple.
          // ``highlighted`` keeps the amber accent (a deliberate semantic highlight).
          // Live-active (running now) → emerald + a pulsing ring (see the CSS below);
          // highlighted (dropdown select) → amber; default → slate. Active wins its
          // colour but a node can be both (selected + running) — active ring shows.
          background: active
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : highlighted
              ? 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
              : 'linear-gradient(135deg, #4a515c 0%, #2f343c 100%)',
          color: 'white',
          border: active
            ? '3px solid #6ee7b7'
            : highlighted
              ? '3px solid #fef08a'
              : selected
                ? '2px solid #6e7682'
                : '1px solid #2f343c',
          boxShadow: active
            ? '0 0 0 3px rgba(16, 185, 129, 0.35), 0 4px 16px rgba(5, 150, 105, 0.45)'
            : highlighted ? '0 0 0 2px rgba(245, 158, 11, 0.35), 0 4px 14px rgba(234, 88, 12, 0.35)' : undefined,
          animation: active ? 'stepflowAgentPulse 1.4s ease-in-out infinite' : undefined,
          borderRadius: 8,
          padding: '6px 10px',
          fontSize: 12,
          fontWeight: 600
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Handle type="target" position={Position.Top} className="!bg-gray-400" />
        <span
          className="block w-full truncate text-center"
          title={label || undefined}
        >
          {label}
        </span>
        <Handle type="source" position={Position.Bottom} className="!bg-gray-400" />
      </div>
      {hasComment &&
        tooltip.show &&
        createPortal(
          <div
            className="fixed z-[99999] pointer-events-none px-4 py-2 text-xs font-normal text-gray-800 bg-white border border-gray-200 rounded-lg shadow-xl"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translate(-50%, calc(-100% - 8px))',
              whiteSpace: 'pre-wrap',
              minWidth: 420,
              maxWidth: 520
            }}
          >
            {comment}
          </div>,
          document.body
        )}
    </>
  );
});
AgentNode.displayName = 'AgentNode';

// Top-to-bottom flow (Dagre `rankdir: TB`). Start emits down, End accepts from above.
const StartNode = memo(({ data }) => (
  <div
    style={{
      // Soft green tint + dark text — same start/end color code, far less flashy.
      background: '#bbf7d0',
      color: '#15803d',
      border: '1.5px solid #86efac',
      borderRadius: '50%',
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 8,
      fontSize: '11px',
      fontWeight: '700'
    }}
  >
    <Handle type="source" position={Position.Bottom} className="!bg-gray-400" />
    {data?.label ?? 'Start'}
  </div>
));
StartNode.displayName = 'StartNode';

const EndNode = memo(({ data }) => (
  <div
    style={{
      // Soft red tint + dark text — same color code, less flashy.
      background: '#fecaca',
      color: '#b91c1c',
      border: '1.5px solid #fca5a5',
      borderRadius: '50%',
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 8,
      fontSize: '11px',
      fontWeight: '700'
    }}
  >
    <Handle type="target" position={Position.Top} className="!bg-gray-400" />
    {data?.label ?? 'End'}
  </div>
));
EndNode.displayName = 'EndNode';

const nodeTypes = { agent: AgentNode, startNode: StartNode, endNode: EndNode };

function nodeMatchesWorkbenchHighlight(node, highlightLogicalId) {
  if (!highlightLogicalId) return false;
  const h = String(highlightLogicalId);
  const label = String(node?.data?.label ?? '');
  if (label && label === h) return true;
  const id = String(node?.id ?? '');
  return id === h || id.endsWith(`__${h}`);
}

const GraphVisualization = ({
  data,
  highlightLogicalId = null,
  /** LIVE run-path: a Set (or array) of logicalIds currently AT PLAY in a running
   *  room. Distinct from ``highlightLogicalId`` (a single dropdown selection that
   *  ALSO camera-fits) — active nodes PULSE and there can be several at once. Empty
   *  / null → no live highlight (the classic build/agents view). */
  activeLogicalIds = null,
  readOnly = false,
  /** Optional reset key — when caller wants the held graph to be wiped
   *  (e.g. switched networks). When omitted the renderer is sticky for
   *  the lifetime of the mount. */
  stickyResetKey = null,
}) => {
  const reactFlowApiRef = useRef(null);
  // Hold the last good graph so a transient empty ``data`` (silent
  // refetch returning no graph, replayed ARTIFACT_EMIT carrying an
  // empty payload, etc.) doesn't visibly wipe the panel.
  const stickyData = useStickyGraph(data, { resetKey: stickyResetKey });
  const [nodes, setNodes, onNodesChange] = useNodesState(stickyData?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(stickyData?.edges || []);

  useEffect(() => {
    if (stickyData?.nodes?.length) {
      setNodes(stickyData.nodes);
      setEdges(stickyData.edges || []);
    }
  }, [stickyData, setNodes, setEdges]);

  /** After layout, gently frame the highlighted agent when the dropdown changes. */
  useEffect(() => {
    const api = reactFlowApiRef.current;
    const list = stickyData?.nodes;
    if (!api || !highlightLogicalId || !list?.length) return;
    const match = list.find((n) => {
      const aslType = n?.data?.aslType;
      if (aslType === 'start' || aslType === 'end') return false;
      return nodeMatchesWorkbenchHighlight(n, highlightLogicalId);
    });
    if (!match?.id) return;
    const id = match.id;
    requestAnimationFrame(() => {
      try {
        api.fitView({
          nodes: [{ id }],
          duration: 220,
          padding: 0.45,
          minZoom: 0.12,
          maxZoom: 1.6,
        });
      } catch {
        /* noop */
      }
    });
  }, [highlightLogicalId, stickyData?.nodes]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const noopApply = useCallback(() => {}, []);

  // Node styles: Start/End circles only (agent nodes use custom AgentNode)
  const getNodeStyle = (node) => {
    const aslType = node.data?.aslType;
    // start/end render via the StartNode/EndNode CUSTOM components, which draw
    // their own circle — return {} so the React Flow wrapper adds NO competing
    // circle behind them (the old style here produced a double-circle). Same
    // reason the agent type returns {} below.
    if (aslType === 'start' || aslType === 'end') {
      return {};
    }
    if (node.type === 'agent') {
      return {}; // AgentNode has its own styling
    }
    return {
      // App theme slate-blue (tailwind custom blue 500→700), not bright indigo.
      background: 'linear-gradient(135deg, #4a515c 0%, #2f343c 100%)',
      color: 'white',
      border: '1px solid #2f343c',
      borderRadius: '8px',
      padding: '10px',
      fontSize: '12px',
      fontWeight: '600'
    };
  };

  /**
   * Style + type the nodes and run the Dagre layout — MEMOIZED.
   *
   * This is expensive (a full graph layout). Both the Build tab and the Agents
   * tab share this component, but the Agents tab's parent (AgentConfigEditor)
   * re-renders on EVERY agent edit — recomputing the layout each time made the
   * diagram visibly flicker / vanish. Memoizing on the real inputs
   * (nodes/edges/highlight) means an unrelated parent re-render no longer
   * re-lays-out the graph, so it stays put like the Build tab's does.
   */
  const styledNodes = useMemo(() => {
    // Normalize the live active-set (accept Set or array) once per render.
    const activeSet = activeLogicalIds instanceof Set
      ? activeLogicalIds
      : new Set(Array.isArray(activeLogicalIds) ? activeLogicalIds : []);

    const styledForLayout = nodes.map((node) => {
      const aslType = node.data?.aslType;
      const type =
        aslType === 'start' ? 'startNode' : aslType === 'end' ? 'endNode' : node.type || 'agent';
      const layer = { ...getNodeStyle(node) };
      const merged = { ...node.style, ...layer };
      const highlighted =
        type === 'agent' && highlightLogicalId
          ? nodeMatchesWorkbenchHighlight(node, highlightLogicalId)
          : false;
      // Live "at play" — any of the active logicalIds matches this node.
      const active =
        type === 'agent' && activeSet.size > 0
          ? [...activeSet].some((lid) => nodeMatchesWorkbenchHighlight(node, lid))
          : false;
      const dataWithHighlight = {
        ...node.data,
        highlighted,
        active,   // AgentNode pulses when true
      };
      if (type === 'agent' && merged.width == null) {
        merged.width = ASL_GRAPH_NODE_WIDTH;
        merged.height = ASL_GRAPH_NODE_HEIGHT;
      }
      return {
        ...node,
        type,
        data: dataWithHighlight,
        style: merged,
      };
    });
    /** Always re-run Dagre TB so backend-baked L→R graphs also flip top-down. */
    return applyDagreTopBottom(styledForLayout, edges);
  }, [nodes, edges, highlightLogicalId, activeLogicalIds]);

  if (!stickyData || !stickyData.nodes || stickyData.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center text-gray-500">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p>No visualization available yet</p>
          <p className="text-sm mt-2">Infrastructure graph will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-blue-50 overflow-hidden">
      {/* Live "at play" pulse for active agent nodes (data.active). Injected once. */}
      <style>{`@keyframes stepflowAgentPulse {
        0%,100% { box-shadow: 0 0 0 3px rgba(16,185,129,0.35), 0 4px 16px rgba(5,150,105,0.45); }
        50%     { box-shadow: 0 0 0 7px rgba(16,185,129,0.15), 0 4px 20px rgba(5,150,105,0.55); }
      }`}</style>
      <ReactFlow
        nodes={styledNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={(instance) => {
          reactFlowApiRef.current = instance;
        }}
        onNodesChange={readOnly ? noopApply : onNodesChange}
        onEdgesChange={readOnly ? noopApply : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        fitView
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: { strokeWidth: 2, stroke: '#94a3b8' },
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e5e7eb" gap={16} />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default GraphVisualization;
